/**
 * AI Models configuration for header dropdown
 * Dynamically generated from aiModels.config.ts to avoid duplication
 */

import openaiIcon from '$lib/../assets/ai-model-icons/openai.png'
import claudeIcon from '$lib/../assets/ai-model-icons/claude.png'
import googleIcon from '$lib/../assets/ai-model-icons/google.png'
import upstageIcon from '$lib/../assets/ai-model-icons/upstage.png'
import openrouterIcon from '$lib/../assets/ai-model-icons/openrouter.png'
import { AI_MODELS } from './aiModels.config'

export interface AIModel {
  id: string
  name: string
  provider: 'openai' | 'anthropic' | 'google' | 'upstage' | 'openrouter'
  icon: string
  description: string
  cost: number
  maxTokens: number
  recommended?: boolean
}

// Map provider icons
const providerIcons: Record<string, string> = {
  openai: openaiIcon,
  anthropic: claudeIcon,
  google: googleIcon,
  upstage: upstageIcon,
  openrouter: openrouterIcon
}

// Convert AI_MODELS from config to flat array for header dropdown
export const aiModels: AIModel[] = Object.entries(AI_MODELS).flatMap(([provider, models]) => 
  models.map(model => ({
    id: model.id,
    name: model.name,
    provider: model.provider as AIModel['provider'],
    icon: typeof model.icon === 'string' && model.icon.length === 2 
      ? model.icon  // Emoji
      : providerIcons[provider] || model.icon,
    description: model.description || '',
    cost: model.costPerToken || 0,
    maxTokens: model.maxTokens || 4096,
    recommended: model.recommended || false
  }))
)

// Group models by provider for easier access
export const modelsByProvider = aiModels.reduce((acc, model) => {
  if (!acc[model.provider]) {
    acc[model.provider] = []
  }
  acc[model.provider].push(model)
  return acc
}, {} as Record<string, AIModel[]>)

// Export provider names for UI
export const providerNames: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google AI',
  upstage: 'Upstage',
  openrouter: 'OpenRouter'
}

// Get model by ID
export function getModelById(modelId: string): AIModel | undefined {
  return aiModels.find(m => m.id === modelId)
}

// Get default model for provider
export function getDefaultModelForProvider(provider: string): AIModel | undefined {
  const models = modelsByProvider[provider]
  return models?.[0]
}

// Filter models by available API keys
export function filterModelsByApiKeys(enabledProviders: string[]): AIModel[] {
  return aiModels.filter(model => enabledProviders.includes(model.provider))
}